import React, { useEffect, useState } from 'react';
import axios from '../config/axiosConfig';
import SEO from '../components/SEO';
import { MdChevronLeft, MdChevronRight, MdCalendarToday, MdCategory, MdList } from 'react-icons/md';

// SlideShow Component for Events Cards
const EventSlideshow = ({ images, title }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!images || images.length === 0) {
        return (
            <div className="w-full h-64 bg-gray-100 flex items-center justify-center text-gray-400 italic rounded-t-2xl border-b select-none">
                No Photos Available
            </div>
        );
    }

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    return (
        <div className="relative w-full h-64 sm:h-72 overflow-hidden bg-slate-950 rounded-t-2xl border-b group">
            {images.map((img, idx) => (
                <div
                    key={img.id}
                    className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${
                        idx === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                >
                    <img
                        src={img.image}
                        alt={`${title} - slide ${idx + 1}`}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 z-20">
                        <span className="text-white text-xs font-semibold px-2 py-0.5 bg-black/40 rounded-full backdrop-blur-sm">
                            {idx + 1} / {images.length}
                        </span>
                    </div>
                </div>
            ))}

            {images.length > 1 && (
                <>
                    {/* Left Arrow */}
                    <button
                        onClick={handlePrev}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2.5 backdrop-blur-md transition-all opacity-0 group-hover:opacity-100 z-30"
                        aria-label="Previous image"
                    >
                        <MdChevronLeft size={24} />
                    </button>
                    {/* Right Arrow */}
                    <button
                        onClick={handleNext}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2.5 backdrop-blur-md transition-all opacity-0 group-hover:opacity-100 z-30"
                        aria-label="Next image"
                    >
                        <MdChevronRight size={24} />
                    </button>

                    {/* Indicators */}
                    <div className="absolute bottom-4 right-4 flex space-x-1.5 z-30">
                        {images.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentIndex(idx);
                                }}
                                className={`w-2.5 h-2.5 rounded-full transition-all ${
                                    idx === currentIndex ? 'bg-blue-500 w-5' : 'bg-white/65 hover:bg-white'
                                }`}
                                aria-label={`Go to slide ${idx + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// Main public Events view
const Events = () => {
    const [events, setEvents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const [expandedEvents, setExpandedEvents] = useState({});

    useEffect(() => {
        fetchEvents();
        fetchCategories();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get('nai-events/events/');
            setEvents(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching public events:", error);
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('nai-events/categories/');
            setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const toggleExpandEvent = (id) => {
        setExpandedEvents(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const filteredEvents = selectedCategory === 'all'
        ? events
        : events.filter(e => e.category === Number(selectedCategory));

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-xl font-bold text-gray-500 animate-pulse">Loading Events...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50">
            <SEO 
                title="Events & Conclaves - Newsmedia Association of India"
                description="Explore conclaves, workshops, conferences, and events hosted by the Newsmedia Association of India (NAI)."
                keywords="NAI events, Media conclave, Journalism workshops, India news association"
            />

            {/* Banner Section */}
            <div 
                className="relative w-full py-20 text-white text-center overflow-hidden bg-cover bg-center"
                style={{ backgroundImage: `url('/images/events_bg_banner.png?v=2')` }}
            >
                {/* Black Overlay with opacity to make text fully legible */}
                <div className="absolute inset-0 bg-slate-950/40 z-0"></div>
                
                <div className="relative z-10 max-w-7xl mx-auto px-4">
                    <h1 className="text-4xl md:text-5xl font-black mb-4 uppercase tracking-tight">Our Events & Conclaves</h1>
                    <p className="text-lg md:text-xl text-slate-200 max-w-2xl mx-auto font-semibold">
                        Highlighting journalism excellence, workshops, and community meetings across India.
                    </p>
                </div>
            </div>

            {/* Content Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Category Filter Sidebar */}
                    <div className="lg:col-span-3 bg-white p-6 rounded-2xl shadow-md border border-gray-100 sticky top-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                            <MdCategory className="text-blue-600" /> Filter by Category
                        </h2>
                        <div className="flex flex-col space-y-1">
                            <button
                                onClick={() => setSelectedCategory('all')}
                                className={`w-full text-left px-4 py-2.5 rounded-xl font-semibold transition-all text-sm flex items-center gap-2 ${
                                    selectedCategory === 'all'
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <MdList size={18} /> All Categories ({events.length})
                            </button>
                            {categories.map((cat) => {
                                const catCount = events.filter(e => e.category === cat.id).length;
                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSelectedCategory(String(cat.id))}
                                        className={`w-full text-left px-4 py-2.5 rounded-xl font-semibold transition-all text-sm flex justify-between items-center ${
                                            selectedCategory === String(cat.id)
                                                ? 'bg-blue-600 text-white shadow-md'
                                                : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="truncate pr-2">{cat.name}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            selectedCategory === String(cat.id) ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {catCount}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Events Grid/List */}
                    <div className="lg:col-span-9 space-y-8">
                        {filteredEvents.length === 0 ? (
                            <div className="bg-white rounded-2xl shadow-md border border-gray-150 p-12 text-center text-gray-500 italic">
                                No events found in this category.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {filteredEvents.map((event) => {
                                    const isExpanded = !!expandedEvents[event.id];
                                    const descLength = event.description ? event.description.length : 0;
                                    const shouldTruncate = descLength > 180;
                                    const displayDesc = shouldTruncate && !isExpanded
                                        ? `${event.description.substring(0, 180)}...`
                                        : event.description;

                                    return (
                                        <div key={event.id} className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 transition-all duration-300 flex flex-col h-full overflow-hidden">
                                            {/* Top Slideshow */}
                                            <EventSlideshow images={event.images} title={event.title} />

                                            {/* Details */}
                                            <div className="p-6 flex flex-col flex-1">
                                                {/* Meta Row */}
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 text-xs font-semibold text-gray-500">
                                                    <span className="flex items-center gap-1.5 uppercase">
                                                        <MdCalendarToday className="text-blue-500" />
                                                        {new Date(event.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    </span>
                                                    {event.category_detail && (
                                                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-100 uppercase tracking-wider text-[10px]">
                                                            {event.category_detail.name}
                                                        </span>
                                                    )}
                                                </div>

                                                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight uppercase">
                                                    {event.title}
                                                </h3>

                                                <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1 whitespace-pre-line">
                                                    {displayDesc}
                                                </p>

                                                {shouldTruncate && (
                                                    <button
                                                        onClick={() => toggleExpandEvent(event.id)}
                                                        className="text-blue-600 hover:text-blue-800 font-bold text-sm text-left hover:underline focus:outline-none"
                                                    >
                                                        {isExpanded ? 'Show Less' : 'Read Full Event Details'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Events;
